import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogService } from '@/services/blog';
import BlogForm from '@/components/admin/BlogForm';
import { BlogPost } from '@/types/blog';
import { toast } from '@/components/ui/sonner';

const BlogFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch categories and authors
  const { data: categories = [], isLoading: loadingCategories } = useQuery({
    queryKey: ['blogCategories'],
    queryFn: blogService.getCategories,
  });
  const { data: authors = [], isLoading: loadingAuthors } = useQuery({
    queryKey: ['blogAuthors'],
    queryFn: blogService.getAuthors,
  });

  // Fetch blog post for edit
  const { data: initialValues, isLoading: loadingPost, error: postError } = useQuery<BlogPost | undefined>({
    queryKey: ['blogPost', id],
    queryFn: () => id ? blogService.getPost(id) : Promise.resolve(undefined),
    enabled: isEdit,
  });

  // Create or update mutation
  const mutation = useMutation({
    mutationFn: async (values: Partial<BlogPost>) => {
      if (isEdit && id) {
        // Update
        await blogService.updatePost(id, values);
      } else {
        // Create
        await blogService.createPost(values);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBlogs']);
      toast('Blog post saved!');
      navigate('/admin/blogs');
    },
    onError: () => {
      toast('Error saving blog post', { className: 'bg-red-50 text-red-600 border-red-200' });
    }
  });

  if (loadingCategories || loadingAuthors || (isEdit && loadingPost)) {
    return <div>Loading...</div>;
  }
  if (postError) {
    return <div>Error loading blog post.</div>;
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">{isEdit ? 'Edit' : 'Add'} Blog Post</h1>
      <BlogForm
        initialValues={initialValues}
        categories={categories}
        authors={authors}
        loading={mutation.isLoading}
        onSubmit={mutation.mutate}
      />
    </div>
  );
};

export default BlogFormPage; 